import { SerializedStyles, Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { SyntheticEvent } from 'react';

import { Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon } from 'chatie-design-system/icon';

import Badge, { useBadgePresets } from '@components/common/Badge';
import RoundAvatar from '@components/common/RoundAvatar';
import SvgIcon from '@components/common/SvgIcon';
import Pad from '@components/layout/Pad';

import IcPlay from '@public/icons/ic_play_white.svg';

import { IChatCharacter, IChatProfile } from '@model';
import { multilineEllipsis } from '@styles/css';
import { formatNumberMillion } from '@utils/chat';
import { getJosaOnly } from '@utils/misc';

const GRADATION_MAIN_COLOR_OPACITY_60 = ' #20aeae99';

const wrapperCSS = (background?: string) => (theme: Theme) =>
  css`
    background: ${background
      ? background
      : `linear-gradient(
      90deg,
      ${theme.colors.main500} 0%,
      ${GRADATION_MAIN_COLOR_OPACITY_60} 100%
    )`};
  `;

const containerCSS = (isPeek: boolean) => (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;

    .name-container {
      ${isPeek ? `height: 30px` : ''};
      display: flex;
      align-items: center;
      ${Typography.body14}
      color:${theme.var.white};
    }
    span {
      white-space: nowrap;
    }
    .name {
      white-space: wrap;
      ${multilineEllipsis(1)};
      margin-left: ${Spacing.xsmall}px;
    }

    .play-count {
      display: flex !important;
      white-space: wrap;
      margin-top: ${Spacing.xxsmall}px;
      ${multilineEllipsis(1)};
      ${Typography.body11};
      align-items: center;
      margin-left: ${Spacing.xsmall}px;
      gap: 2px;
    }

    .enter-button {
      display: flex;
      margin-left: ${Spacing.small}px;
      align-items: center;
      gap: ${Spacing.xxsmall}px;
      ${Typography.body12}
      color:${theme.var.white}
    }

    .enter-text-container {
      display: flex;
      flex-direction: column;
    }
  `;

const badgeCSS = css`
  border-radius: 8px;
  height: 16px;
  width: 16px;
`;

interface IChatEnterPad {
  character: IChatCharacter | IChatProfile;
  onClickChatEnter?: (character: IChatCharacter | IChatProfile) => void;
  enterText?: string;
  wrapperCss?: SerializedStyles;
  isNew?: boolean;
  playCount?: number;
  onlyName?: boolean;
  title?: string;
  background?: string;
  isPeek?: boolean;
}

const ChatEnterPad = ({
  character,
  onClickChatEnter,
  enterText = '입장',
  wrapperCss,
  isNew,
  playCount,
  onlyName = false,
  title,
  isPeek = false,
  background,
}: IChatEnterPad) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { new: newbadge } = useBadgePresets();

  return (
    <div css={[wrapperCSS(background), wrapperCss]}>
      <Pad
        onClick={(e: SyntheticEvent) => {
          if (onClickChatEnter) {
            e.stopPropagation();
            onClickChatEnter(character);
          }
        }}
        className={'chat-enter-pad'}
      >
        <div css={containerCSS(isPeek)}>
          <div className="name-container">
            {!isPeek && (
              <RoundAvatar size={30} picture={character.profile_image} />
            )}
            <div className="enter-text-container">
              <div className="name-container">
                {!isPeek && (
                  <div className="name">
                    {t('{{characterName}}', {
                      characterName: character.name,
                    })}
                  </div>
                )}
                {!onlyName && (
                  <span>
                    {title
                      ? title
                      : t(`${getJosaOnly(character.name, '과')} 대화하기`)}
                  </span>
                )}
              </div>
              {(!!playCount || playCount === 0) && (
                <div className="play-count">
                  <SvgIcon icon={IcPlay} colorPropety="white" />
                  {formatNumberMillion(playCount)}
                </div>
              )}
            </div>
          </div>
          <button className="enter-button">
            <span>{t(`${enterText}`)}</span>
            {isNew && <Badge text={t('N')} {...newbadge} css={badgeCSS} />}
            <Icon
              icon={ArrowRight}
              size={Sizes.small}
              color={theme.var.white}
            />
          </button>
        </div>
      </Pad>
    </div>
  );
};

export default ChatEnterPad;
